<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="800px"
        transition="slide-y-transition"
        eager
        :persistent="actionLoading"
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template>
                        Edycja zbiorcza komponentów
                    </template>
                </h1>
            </v-card-title>

            <v-data-table
                :headers="headers"
                :items="productComponents"
                :mobile-breakpoint="0"
                hide-default-footer
                disable-pagination
                name="productComponentsData"
            >
                <template #item.productName="{item}">
                    {{ item.productName }}
                </template>
                <template #item.foldingType="{item}">
                    {{ item.foldingType }}
                </template>
                <template #item.componentName="{item}">
                    {{ item.component.name }}
                </template>
                <template #item.printTaskParts="{item}">
                    <v-text-field
                        :value="item.printTaskParts"
                        type="number"
                        min="1"
                        dense
                        outlined
                        hide-details
                        :error="printTaskPartsErrors(item.index)"
                        data-sentry-label="Zmiana użytków"
                        @change="changeParts(item, $event)"
                    />
                </template>
                <template #item.amount="{item}">
                    {{ item.amount }}
                </template>
                <template #item.impositionId="{item}">
                    <v-select
                        :value="item.impositionIndex"
                        :items="impositions"
                        item-value="index"
                        item-text="text"
                        outlined
                        dense
                        clearable
                        hide-details
                        return-object
                        data-sentry-label="Zmiana montażu"
                        @change="changeImposition(item, $event)"
                    />
                </template>
            </v-data-table>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    :disabled="actionLoading"
                    data-sentry-label="Anuluj"
                    @click="onClose"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    color="success"
                    text
                    :loading="actionLoading"
                    data-sentry-label="Zapisz"
                    @click="onSave"
                >
                    Zapisz
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import parse from '@/mixins/parse'
import { appConfig } from '@/config'

export default {
    name: 'ManageComponentsDialog',
    mixins: [ parse ],
    props: {
        isOpen: {
            type: Boolean
        },
        productComponentsData: {
            type: Array,
            default: null
        },
        impositionsData: {
            type: Array,
            default: null
        },
        actionLoading: {
            type: Boolean
        }
    },
    data() {
        return {
            impositions: [],
            productComponents: [],
            componentsToImpositions: [],
            headers: [
                {
                    text: 'Nazwa produktu',
                    value: 'productName',
                    sortable: false,
                },
                {
                    text: 'Rodzaj składania',
                    value: 'foldingType',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Komponent',
                    value: 'componentName',
                    sortable: false,
                },
                {
                    text: 'Ilość użytków',
                    value: 'printTaskParts',
                    sortable: false,
                    align: 'center',
                    width: 120
                },
                {
                    text: 'Nakład',
                    value: 'amount',
                    sortable: false,
                    align: 'center',
                    width: 150
                },
                {
                    text: 'Montaż',
                    value: 'impositionId',
                    sortable: false,
                    width: 200
                }
            ]
        }
    },
    validations() {
        const rules = {
            productComponents: {
                $each: {
                    printTaskParts: {
                        required: (value, productComponent) => {
                            if (productComponent.impositionId) {
                                return !!value
                            }

                            return true

                        }
                    }
                }
            }
        }

        return rules
    },
    watch: {
        isOpen(boolean) {
            if (boolean) {
                this.impositions = this.impositionsData.map((item, index) => ({
                    ...item,
                    text: `Montaż ${index + 1}`,
                    index: index
                }))

                this.componentsToImpositions = this.impositionsData.map((item) => ({
                    components: [],
                    impositionId: item.id
                }))

                this.productComponentsData.forEach(product => {
                    product.orderProductComponents.forEach((component) => {
                        let impositionIndex = null

                        const indexedImposition = this.impositions.find(imposition => {
                            return imposition.id === component.impositionId
                        })

                        if (indexedImposition) impositionIndex = indexedImposition.index

                        const foldingTypeProperty = component.orderComponentProperties.find(
                            prop => prop.productProductionPropertyId === appConfig.common.productProductionPropertyIds.foldingType
                        )
                        const foldingType = foldingTypeProperty ? foldingTypeProperty.value : null

                        this.productComponents.push({
                            ...component,
                            amount: product.amount,
                            impositionIndex: impositionIndex,
                            foldingType
                        })
                    })
                })

                this.productComponents = this.productComponents.map((component, index) => ({
                    ...component,
                    index
                }))
            } else {
                setTimeout(() => {
                    this.impositions = []
                    this.productComponents = []
                    this.componentsToImpositions = []
                    this.$v.$reset()
                }, 500)
            }
        }
    },
    methods: {
        changeParts(component, value) {
            component.printTaskParts = this.parseEmptyStringToNull(value)
        },
        changeImposition(component, imposition) {
            if (imposition) {
                component.impositionId = imposition.id
                component.impositionIndex = imposition.index
            } else {
                component.impositionId = null
                component.impositionIndex = null
            }
        },
        onSave() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })
            } else {
                this.productComponents.forEach((component) => {
                    const index = this.componentsToImpositions.findIndex(item => {

                        return item.impositionId === component.impositionId
                    })

                    if (index > -1) {
                        this.componentsToImpositions[index].components.push({
                            'parts': component.printTaskParts,
                            'componentId': component.id
                        })
                    }
                })

                this.$emit('on-save', this.componentsToImpositions)
            }
        },
        onClose() {
            if (!this.actionLoading) {
                this.$emit('on-close')
            }
        },
        printTaskPartsErrors(index) {
            if (!this.$v.productComponents.$each[index].printTaskParts.$dirty) return

            return !this.$v.productComponents.$each[index].printTaskParts.required
        },
    }
}
</script>
